import { useRouter } from 'next/router';
import { useSession } from 'next-auth/react';
import { useEffect, useRef } from 'react';

import { useMetrics } from '../../hooks/useMetrics';
import Metrics from '../../services/metrics';

/**
 * Emits a Mixpanel pageview (`APP.Route.Change`) for every visitor — anonymous
 * and authenticated alike — on initial load and on each client-side route
 * change. This is the single global pageview source (it replaces the legacy
 * Plausible pageviews and the authenticated-only tracking in useIdentifyMetrics).
 */
const RouterAnalyticsProvider = ({ children }: { children: React.ReactChild }) => {
  const router = useRouter();
  const session = useSession();
  // Unauthenticated logger so anonymous visitors are tracked too.
  const logger = useMetrics(true);
  const hasTrackedInitial = useRef(false);

  const logPageview = () =>
    logger.logEvent('Route.Change', {
      pathname: window.location.pathname,
      url: router.pathname,
      search: window.location.search,
    });

  // Keep a ref to the latest logPageview. The routeChangeComplete handler below
  // is registered once (deps are only the stable `router.events`), so without
  // this it would call a mount-time closure and report a stale `url`
  // (router.pathname) on every later navigation.
  const logPageviewRef = useRef(logPageview);
  useEffect(() => {
    logPageviewRef.current = logPageview;
  });

  useEffect(() => {
    // Register the landing page as early as possible — on mount, before any
    // pageview can fire — so every event carries the true entry page.
    // `register_once` is idempotent, so running it ahead of the gated initial
    // pageview is safe.
    Metrics.registerLandingPageOnce();
  }, []);

  useEffect(() => {
    // Hold the very first pageview until NextAuth has resolved. For an
    // authenticated user the identify effect (mounted deeper in the tree, so
    // its effect runs first) has set the `uid` super-property by the time this
    // re-runs — so the initial pageview carries `uid`, matching the previous
    // behaviour. Anonymous visitors resolve to `unauthenticated` and are still
    // tracked.
    if (session.status === 'loading' || hasTrackedInitial.current) return;
    hasTrackedInitial.current = true;
    logPageviewRef.current();
  }, [session.status]);

  useEffect(() => {
    // Only emit route-change pageviews once the initial pageview has fired.
    // This preserves ordering and prevents a navigation during the brief
    // session-loading window from emitting a pageview before the initial one.
    // Any navigation that happens before then is captured by the gated initial
    // effect above (which logs whatever page the user has landed on).
    const handleRouteChange = () => {
      if (hasTrackedInitial.current) logPageviewRef.current();
    };
    router.events.on('routeChangeComplete', handleRouteChange);
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router.events]);

  return <>{children}</>;
};
export default RouterAnalyticsProvider;
